import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ConfigProvider, Spin, Alert } from 'antd';
import TicketDetail from './components/TicketDetail';
import { apiService } from './services/api';
import './App.css';

// Configure Ant Design theme
const theme = {
  token: {
    colorPrimary: '#0088ff',
    borderRadius: 6,
  },
};

function App() {
  return (
    <ConfigProvider theme={theme}>
      <Router>
        <div className="App">
          <Routes>
            <Route 
              path="/in-app/ticket/:ticketId" 
              element={<TicketDetailWrapper />} 
            />
            <Route 
              path="/in-app/ticket/:ticketId/accept" 
              element={<TicketAcceptWrapper />} 
            />
            <Route 
              path="/in-app/ticket/:ticketId/reject" 
              element={<TicketRejectWrapper />} 
            />
          </Routes>
        </div>
      </Router>
    </ConfigProvider>
  );
}

// Wrapper component to extract ticketId and token from URL params
const TicketDetailWrapper: React.FC = () => {
  const navigate = useNavigate();
  const pathname = window.location.pathname;
  const ticketId = pathname.includes('/ticket/') ? pathname.split('/ticket/')[1] : null;
  
  // Handle URL encoding issues and support both 'token' and 't' parameters
  let token: string | null = null;
  let successType: string | null = null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('t') || urlParams.get('token'); // Support both 't' and 'token'
    successType = urlParams.get('success'); // Get success parameter
  } catch (error) {
    console.error('Error parsing URL parameters:', error);
  }
  
  if (!ticketId) {
    return <div>Invalid ticket ID</div>;
  }

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <TicketDetail 
      ticketId={ticketId} 
      token={token} 
      onBack={handleBack}
      showAcceptSuccess={successType === 'accept'}
      showRejectSuccess={successType === 'reject'}
    />
  );
};

// Wrapper component for auto-accept route
const TicketAcceptWrapper: React.FC = () => {
  const pathname = window.location.pathname;
  const ticketId = pathname.includes('/ticket/') ? pathname.split('/ticket/')[1].replace('/accept', '') : null;

  let token: string | null = null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('t') || urlParams.get('token');
  } catch (error) {
    console.error('Error parsing URL parameters:', error);
  }

  useEffect(() => {
    const autoAccept = async () => {
      if (!ticketId) {
        return;
      }

      let isSuccess = false;
      try {
        if (token) {
          await apiService.acceptTicket(ticketId, token);
          isSuccess = true;
        }
      } catch (err) {
        console.error('Error accepting ticket:', err);
      } finally {
        const baseUrl = window.location.origin;
        const params = new URLSearchParams();
        if (token) params.set('t', token);
        if (isSuccess) params.set('success', 'accept');
        const query = params.toString();
        const redirectUrl = `${baseUrl}/in-app/ticket/${ticketId}${query ? `?${query}` : ''}`;
        window.location.href = redirectUrl;
      }
    };

    autoAccept();
  }, [ticketId, token]);

  return (
    <div className="loading-container">
      <Spin size="large" />
      <div className="loading-text">Accepting ticket...</div>
    </div>
  );
};

// Wrapper component for auto-reject route
const TicketRejectWrapper: React.FC = () => {
  const pathname = window.location.pathname;
  const ticketId = pathname.includes('/ticket/') ? pathname.split('/ticket/')[1].replace('/reject', '') : null;

  let token: string | null = null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('t') || urlParams.get('token');
  } catch (error) {
    console.error('Error parsing URL parameters:', error);
  }

  useEffect(() => {
    const autoReject = async () => {
      if (!ticketId) {
        return;
      }

      let isSuccess = false;
      try {
        if (token) {
          await apiService.rejectTicket(ticketId, token);
          isSuccess = true;
        }
      } catch (err) {
        console.error('Error rejecting ticket:', err);
      } finally {
        const baseUrl = window.location.origin;
        const params = new URLSearchParams();
        if (token) params.set('t', token);
        if (isSuccess) params.set('success', 'reject');
        const query = params.toString();
        const redirectUrl = `${baseUrl}/in-app/ticket/${ticketId}${query ? `?${query}` : ''}`;
        window.location.href = redirectUrl;
      }
    };

    autoReject();
  }, [ticketId, token]);

  return (
    <div className="loading-container">
      <Spin size="large" />
      <div className="loading-text">Rejecting ticket...</div>
    </div>
  );
};

export default App;
